import { makeAutoObservable, runInAction } from 'mobx';
import type RootStore from './RootStore';
import { Service } from '@feathersjs/feathers';
import EventEmitter from 'events';
import { ParamValuesType } from 'types/RouteType';

export interface ChannelServiceResponse {
  data?: ChannelItem[];
  result?: {
    data?: ChannelItem[];
  };
}

export interface UpdatedChannel {
  connectionId?: string;
  routeName: string;
  reloadApp?: boolean;
  params?: Record<string, unknown>;
  queryParams?: Record<string, unknown>;
}

export interface ChannelItem {
  connectionId: string;
  routeChannels?: string[];
  appName?: string;
  appVersion?: string;
  userId?: number;
  deviceId?: string;
  loggedIn: boolean;
  createdDate?: string;
  createdTime?: string;
  updatedDate?: string;
  updatedTime?: string;
}

interface ChannelStoreOptions {
  debugMode: boolean;
  disableCreatedListener?: boolean;
  disableUpdatedListener?: boolean;
  disablePatchedListener?: boolean;
  disableRemovedListener?: boolean;
}

class ChannelStore {
  private options: Record<string, unknown> = {};
  private channelService!: Service<ChannelItem> & EventEmitter;
  private debugMode: boolean;
  private rootStore: RootStore;
  private websocketConnectionId: string = '';
  private items: ChannelItem[] = [];

  public errorOnFind: Error | null = null;
  public errorOnUpdate: Error | null = null;
  public isFindPending = false;
  public isUpdatePending = false;

  constructor(rootStore: RootStore, options: ChannelStoreOptions) {
    if (!rootStore.authStore) {
      throw new Error('Cannot initialize channel store without an authStore');
    }

    this.rootStore = rootStore;
    this.debugMode = options.debugMode || false;
    makeAutoObservable<this, '_rootStore'>(this);

    this.initializeWebsocketConnection();
    this.setupChannelService();
  }

  private initializeWebsocketConnection(): void {
    if (this.rootStore.app?.io?.id) {
      this.setWebsocketConnection(this.rootStore.app.io.id);
    }
  }

  private setupChannelService(): void {
    if (this.rootStore.mode === 'test') return;

    this.channelService = this.rootStore.app.service('/channel');

    this.channelService.on('created', this.addOrUpdateEvent);
    this.channelService.on('patched', this.addOrUpdateEvent);
    this.channelService.on('updated', this.handleUpdateChannel);
    this.channelService.on('removed', this.handleRemoveEvent);
    this.channelService.on('removeAnonymous', this.handleRemoveEvent);
  }

  public dispose = (): void => {
    if (!this.channelService) return;

    this.channelService.removeListener('created', this.addOrUpdateEvent);
    this.channelService.removeListener('patched', this.addOrUpdateEvent);
    this.channelService.removeListener('updated', this.handleUpdateChannel);
    this.channelService.removeListener('removed', this.handleRemoveEvent);
    this.channelService.removeListener(
      'removeAnonymous',
      this.handleRemoveEvent
    );
  };

  public handleUpdateChannel = async (
    updated: UpdatedChannel
  ): Promise<void> => {
    if (
      !updated?.connectionId ||
      !this.currentConnections.has(updated.connectionId)
    ) {
      return;
    }

    if (updated.routeName && this.rootStore?.routerStore) {
      const { routerStore } = this.rootStore;
      const { routeName, params, queryParams } = updated;
      const nextRouteName = routeName.replaceAll(' ', '');

      if (routerStore.routes[nextRouteName]) {
        routerStore.goTo(
          nextRouteName,
          params as ParamValuesType,
          queryParams as ParamValuesType
        );
      }
    }

    if (updated.reloadApp) {
      this.rootStore?.viewStore?.reloadApp();
    }
  };

  public setWebsocketConnection = (value: string): void => {
    this.websocketConnectionId = value;
  };

  private addOrUpdateEvent = (item: ChannelItem): void => {
    const index = this.items.findIndex(
      (existingItem) => existingItem.connectionId === item.connectionId
    );

    if (index === -1) {
      this.items.push(item);
    } else {
      this.items[index] = { ...this.items[index], ...item };
    }
  };

  private handleRemoveEvent = (data: { connectionId: string }): void => {
    if (!data) return;

    const { connectionId } = data;
    this.items = this.items.filter(
      (item) => item.connectionId !== connectionId
    );

    if (connectionId === this.websocketConnection) {
      this.setWebsocketConnection('');
    }
  };

  public async find(
    params: Record<string, unknown> = {}
  ): Promise<ChannelServiceResponse> {
    if (!this.channelService) {
      throw new Error('Channel service not available');
    }

    this.isFindPending = true;

    try {
      const response = (await this.channelService.find(
        params
      )) as ChannelServiceResponse;

      runInAction(() => {
        const items = response?.data || response?.result?.data || [];
        items.forEach(this.addOrUpdateEvent);
        this.isFindPending = false;
      });

      return response;
    } catch (error) {
      this.errorOnFind =
        error instanceof Error ? error : new Error(String(error));
      this.isFindPending = false;
      throw error;
    }
  }

  public async resetChannelItems(): Promise<void> {
    this.items = [];
  }

  get currentConnections(): Set<string> {
    const connections = new Set<string>();

    if (this.rootStore?.app?.io?.id) {
      connections.add(this.rootStore.app.io.id);
    }
    if (this.rootStore?.authStore?.sessionId) {
      connections.add(this.rootStore.authStore.sessionId);
    }
    if (this.websocketConnection) {
      connections.add(this.websocketConnection);
    }

    return connections;
  }

  get websocketConnection(): string {
    return this.websocketConnectionId;
  }

  get entities(): ChannelItem[] {
    return [...this.items];
  }
}

export default ChannelStore;
